import { motion } from "framer-motion";
import { memo, useMemo } from "react";
import Loader from "../feedback/Loader.tsx";
import { RADIUS } from "../radius.ts";
import { SPACINGS } from "../spacings.ts";
import Icon, { IconNames } from "./Icon.tsx";

const themeStyles = {
	primary: {
		initial: {
			backgroundColor: "#0946b8",
			border: "none",
		},
		whileHover: {
			backgroundColor: "#0056b3",
			border: "none",
		},
	},
	danger: {
		initial: {
			backgroundColor: "#FF0000",
			border: "none",
		},
		whileHover: {
			backgroundColor: "#640A0A",
			border: "none",
		},
	},
	secondary: {
		initial: {
			backgroundColor: "#6c757d",
			border: "none",
		},
		whileHover: {
			backgroundColor: "#5a6268",
			border: "none",
		},
	},
	white: {
		initial: {
			backgroundColor: "#FFFFFF",
			color: "#0946B8",
			border: "1px solid #0946B8",
		},
		whileHover: {
			backgroundColor: "#a1ceff",
			color: "#0946B8",
			border: "1px solid #0056b3",
		},
	},
	green: {
		initial: {
			backgroundColor: "#006300",
			border: "none",
		},
		whileHover: {
			backgroundColor: "#004d00",
			border: "none",
		},
	},
};

interface ButtonProps {
	onClick: () => void;
	label: string;
	theme?: "primary" | "danger" | "secondary" | "green";
	size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
	radius?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
	fullWidthContent?: boolean;
	disabled?: boolean;
	loading?: boolean;
	icon?: IconNames;
	iconPosition?: "esquerda" | "direita";
	iconSize?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
	iconTheme?: "dark" | "light";
	loaderTheme?: "dark" | "light";
}

function Button({
	onClick,
	theme = "primary",
	size = "sm",
	radius = "sm",
	loading = false,
	label,
	fullWidthContent = false,
	disabled = false,
	icon,
	iconPosition = "esquerda",
	iconSize = "sm",
	iconTheme = "dark",
	loaderTheme = "dark",
}: ButtonProps) {
	const changeableStyles = useMemo(() => {
		return themeStyles[theme] || themeStyles.primary;
	}, [theme]);

	return (
		<motion.div
			style={{
				width: fullWidthContent ? "100%" : "fit-content",
				pointerEvents: "all",
			}}
			initial={{ opacity: 0.5 }}
			animate={{ opacity: disabled ? 0.3 : 1 }}
			transition={{ duration: 0.3 }}
		>
			<motion.button
				type="submit"
				initial={changeableStyles.initial}
				whileHover={{
					scale: disabled || loading ? 1 : 1.02,
					backgroundColor: changeableStyles.whileHover.backgroundColor,
					border: changeableStyles.whileHover.border
				}}
				whileTap={{ scale: disabled || loading ? 1 : 0.97 }}
				animate={changeableStyles.initial}
				transition={{ duration: 0.1, ease: [0.6, 0.00, 0.2, 1] }}
				disabled={disabled || loading}
				onClick={onClick}
				style={{
					color: "#fff",
					backgroundColor: changeableStyles.initial.backgroundColor,
					width: fullWidthContent ? "100%" : "fit-content",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					border: changeableStyles.initial.border,
					borderRadius: RADIUS[radius],
					padding: SPACINGS[size],
					cursor: loading || disabled ? "default" : "pointer",
					gap: "12px"
				}}
			>
				<div style={{ width: SPACINGS.sm }} />
				{icon && iconPosition === "esquerda" && (
					<div style={{ marginTop: 2 }}>
						<Icon size={iconSize} name={icon} theme={iconTheme} />
					</div>
				)}
				<p
					style={{
						fontSize: "16px",
						fontWeight: 400,
					}}
				>{label}</p>
				{icon && iconPosition === "direita" && (
					<div style={{ marginTop: 2 }}>
						<Icon size={iconSize} name={icon} theme={iconTheme} />
					</div>
				)}
				<div style={{ width: SPACINGS.sm }}>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: loading ? 1 : 0 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
					>
						<Loader theme={loaderTheme} />
					</motion.div>
				</div>
			</motion.button>
		</motion.div>
	);
}

export default memo(Button);
